import { type Attributes, type DataBlock } from 'src/api/generated/ApiClient';
import { type OtherSession } from 'src/types/classSession';

import { formatSessionDate } from './formatSessionDate';

export const findResourceForInstance = (included: DataBlock[], instanceId: string): DataBlock | undefined => {
  return included.find((item) => item.type === 'resource' && item.instanceId === instanceId);
};

export const isEnrolledInInstance = (courseData: DataBlock, instanceId: string): boolean => {
  return courseData.relationships?.enrollment?.data?.instanceId === instanceId;
};

export const extractSessionDateInfo = (attrs: Attributes) => {
  const dateStart = attrs.dateStart || null;
  const dateEnd = attrs.completionDeadline || null;
  const duration = attrs.desiredDuration || 0;
  const formatted = formatSessionDate(dateStart, dateEnd, duration);

  return { dateStart, dateEnd, duration, ...formatted };
};

export const extractOtherSessions = (
  included: DataBlock[],
  currentInstanceId: string,
  courseId: string,
): OtherSession[] => {
  const now = new Date();

  return included
    .filter(
      (item) =>
        item.type === 'resource' &&
        item.instanceId &&
        item.instanceId !== currentInstanceId &&
        item.attributes?.dateStart &&
        new Date(item.attributes.dateStart) >= now,
    )
    .sort((a, b) => new Date(a.attributes!.dateStart!).getTime() - new Date(b.attributes!.dateStart!).getTime())
    .map((resource) => ({
      id: resource.instanceId!,
      ...extractSessionDateInfo(resource.attributes!),
      detailsUrl: `/class/${courseId}/session/${resource.instanceId}`,
      partnerSiteUrl: 'https://training.atlassian.com', // TODO: Replace with actual partner URL
    }));
};

/**
 * Finds the next upcoming instance by looking at resources with dateStart.
 * Returns null if no future scheduled sessions exist.
 */
export const findNextUpcomingInstanceId = (included: DataBlock[]): string | null => {
  const now = new Date();

  const upcomingResource = included
    .filter(
      (item) =>
        item.type === 'resource' &&
        item.instanceId &&
        item.attributes?.dateStart &&
        new Date(item.attributes.dateStart) >= now,
    )
    .sort((a, b) => new Date(a.attributes!.dateStart!).getTime() - new Date(b.attributes!.dateStart!).getTime())[0];

  return upcomingResource?.instanceId || null;
};
